import { InvariantViolation } from '@shared-kernel/domain-error.js';

export const MoneyCurrency = {
  TWD: 'TWD',
} as const;

export type MoneyCurrency = (typeof MoneyCurrency)[keyof typeof MoneyCurrency];

const SUPPORTED_CURRENCIES: readonly MoneyCurrency[] = Object.freeze([MoneyCurrency.TWD]);

export class Money {
  private constructor(
    public readonly amount: number,
    public readonly currency: MoneyCurrency,
  ) {
    Object.freeze(this);
  }

  static of(params: { amount: number; currency: MoneyCurrency }): Money {
    const { amount, currency } = params;
    if (typeof amount !== 'number' || !Number.isFinite(amount)) {
      throw new InvariantViolation('Money: amount must be a finite number', { amount });
    }
    if (amount < 0) {
      throw new InvariantViolation('Money: amount must be non-negative', { amount });
    }
    if (!Number.isInteger(amount)) {
      throw new InvariantViolation('Money: amount must be integer (minor currency unit)', {
        amount,
      });
    }
    if (!SUPPORTED_CURRENCIES.includes(currency)) {
      throw new InvariantViolation('Money: unsupported currency', { currency });
    }
    return new Money(amount, currency);
  }

  static zero(currency: MoneyCurrency): Money {
    return new Money(0, currency);
  }

  plus(other: Money): Money {
    if (other.currency !== this.currency) {
      throw new InvariantViolation('Money: currency mismatch on plus', {
        left: this.currency,
        right: other.currency,
      });
    }
    return new Money(this.amount + other.amount, this.currency);
  }

  minus(other: Money): Money {
    if (other.currency !== this.currency) {
      throw new InvariantViolation('Money: currency mismatch on minus', {
        left: this.currency,
        right: other.currency,
      });
    }
    if (this.amount < other.amount) {
      throw new InvariantViolation('Money: subtraction would go negative', {
        left: this.amount,
        right: other.amount,
      });
    }
    return new Money(this.amount - other.amount, this.currency);
  }

  /**
   * Multiply by a non-negative scalar. Used for line totals (price × quantity)
   * and percentage calculations (percentOf below). Rounds to integer via Math.round
   * because minor-unit currencies (TWD) don't allow fractions.
   */
  times(factor: number): Money {
    if (!Number.isFinite(factor) || factor < 0) {
      throw new InvariantViolation('Money: times factor must be finite ≥ 0', { factor });
    }
    return new Money(Math.round(this.amount * factor), this.currency);
  }

  /**
   * Return `percent%` of this amount (rounded). Useful for service charge /
   * tip / discount percentages. `percent=10` → this × 0.10.
   */
  percentOf(percent: number): Money {
    if (!Number.isFinite(percent) || percent < 0) {
      throw new InvariantViolation('Money: percent must be finite ≥ 0', { percent });
    }
    return new Money(Math.round((this.amount * percent) / 100), this.currency);
  }

  isZero(): boolean {
    return this.amount === 0;
  }

  gte(other: Money): boolean {
    if (other.currency !== this.currency) {
      throw new InvariantViolation('Money: currency mismatch on gte', {
        left: this.currency,
        right: other.currency,
      });
    }
    return this.amount >= other.amount;
  }

  equals(other: Money): boolean {
    return this.currency === other.currency && this.amount === other.amount;
  }
}
